/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import objectHash from 'object-hash';
import isNil from 'lodash/isNil';

import CommunicatorNumber from '../number/communicator-number';
import CommunicatorUtility from '../utility/communicator-utility';

/**
 * Komunikátor vrstev šablon pro Ki-Wi Server.
 */
class CommunicatorLayer {
  /**
   * Vygeneruje identifikátor vrstvy.
   * @param {DsId} parentLayoutId Identifikátor šablony, do které vrstva patří.
   * @param {number} index Pořadí vrstvy v šabloně.
   * @private
   * @returns {string} Identifikátor.
   */
  generateId(parentLayoutId, index) {
    if (isNil(parentLayoutId)) {
      throw new TypeError('Missing parent layout id.');
    }
    if (isNil(index)) {
      throw new TypeError('Missing layer index.');
    }
    return objectHash({ index, parentLayoutId });
  }

  /**
   * Načte vrstvu z JSON.
   * @param {Store} store Datový sklad ke vložení vrstvy.
   * @param {Object} json Část odpovědi na obsah zařízení s vrstvou.
   * @param {DsId} parentLayoutId Identifikátor šablony, do které vrstva patří.
   * @param {number} index Pořadí vrstvy v šabloně.
   * @returns {Layer} Vrstva.
   */
  parse(store, json, parentLayoutId, index) {
    return store.inject('layer', {
      height: this.parsePercentage(json.height),
      id: this.generateId(parentLayoutId, index),
      index,
      left: this.parsePercentage(json.posX),
      mediumId: CommunicatorUtility.parseReference(json.ref, 'media', {
        nil: null
      }),
      parentLayoutId,
      playlistId: CommunicatorUtility.parseReference(json.ref, 'playlist', {
        nil: null
      }),
      top: this.parsePercentage(json.posY),
      width: this.parsePercentage(json.width),
      zIndex: json.posZ
    });
  }

  /**
   * Načte počet procent v
   * [CSS formátu](https://developer.mozilla.org/en-US/docs/Web/CSS/percentage).
   * @param {string} json Část odpovědi na obsah zařízení s procenty.
   * @private
   * @returns {string} Počet procent.
   */
  parsePercentage(json) {
    const number = CommunicatorNumber.fromString(json, { nil: null });
    return `${number}%`;
  }
}

export default CommunicatorLayer;
